// src/pages/User/AllUsers.tsx

import {
  AddTwoTone,
  ExpandLessTwoTone,
  ExpandMoreTwoTone,
  InfoTwoTone,
  MoreVertTwoTone,
  PeopleTwoTone,
  SearchTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Collapse,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ServiceName, useServiceName } from '@/hooks/useServiceName'
import { UserPool } from '@/types/domain/user'

import { ASC, DESC, isFilterMatch } from '../../api'
import {
  AM,
  AMItem,
  BtnAddUser,
  BtnClearFilter,
  BtnPoolInfo,
  BtnRefresh,
  DlgUserPoolInfo,
  DomainFilterSelector,
  HitsInfo,
  HStack,
  KPage,
  KTextField,
  UserRoleSelector,
  UserRow,
  UserStatusSelector,
} from '../../components'
import { languageConstants as LC } from '../../constants'
import { useUserApi, useUserPool } from '../../hooks'

export type UsersQueryState = {
  filter: string
  order: 'asc' | 'desc'
  orderby:
    | 'firstname'
    | 'lastname'
    | 'entity_id'
    | 'email'
    | 'created_at'
    | 'updated_at'
  poolInfo: boolean
  role: string[]
  status: string[]
}

const DEFAULT_QUERY: UsersQueryState = {
  filter: '',
  order: 'asc',
  orderby: 'lastname',
  poolInfo: false,
  role: ['guest', 'user', 'coach', 'admin'],
  status: [LC.USERSTATUS_ACTIVE, LC.USERSTATUS_INACTIVE],
}

export const useUsersQueryState = () => {
  const [params, setParams] = useSearchParams()

  const state: UsersQueryState = useMemo(() => {
    const getArray = (key: string) => {
      const raw = params.get(key)
      if (!raw) return []

      return raw.split(',').filter(Boolean)
    }

    return {
      filter: params.get('filter') ?? DEFAULT_QUERY.filter,
      order:
        (params.get('order') as UsersQueryState['order']) ??
        DEFAULT_QUERY.order,
      orderby:
        (params.get('orderby') as UsersQueryState['orderby']) ??
        DEFAULT_QUERY.orderby,
      poolInfo: params.get('poolInfo') === '1',
      role: params.has('role') ? getArray('role') : DEFAULT_QUERY.role,
      status: params.has('status') ? getArray('status') : DEFAULT_QUERY.status,
    }
  }, [params])

  const update = useCallback(
    (patch: Partial<UsersQueryState>) => {
      const next = new URLSearchParams(params)

      const setOrDelete = (key: string, value?: string) => {
        if (!value) next.delete(key)
        else next.set(key, value)
      }

      if (patch.filter !== undefined)
        setOrDelete('filter', patch.filter || undefined)

      if (patch.order !== undefined) setOrDelete('order', patch.order)

      if (patch.orderby !== undefined) setOrDelete('orderby', patch.orderby)

      if (patch.poolInfo !== undefined)
        setOrDelete('poolInfo', patch.poolInfo ? '1' : undefined)

      if (patch.role !== undefined) {
        const canon = (arr: string[]) => [...arr].sort().join(',')
        const nextVal = canon(patch.role)
        const defVal = canon(DEFAULT_QUERY.role)

        if (nextVal === defVal) next.delete('role')
        else next.set('role', nextVal)
      }

      if (patch.status !== undefined) {
        const canon = (arr: string[]) => [...arr].sort().join(',')
        const nextVal = canon(patch.status)
        const defVal = canon(DEFAULT_QUERY.status)

        if (nextVal === defVal) next.delete('status')
        else next.set('status', nextVal)
      }

      setParams(next, { replace: true })
    },
    [params, setParams]
  )

  return [state, update] as const
}

const SortArrow = ({ children, order, orderby }) => (
  <>
    {children === orderby && order === ASC && <ExpandLessTwoTone />}
    {children === orderby && order === DESC && <ExpandMoreTwoTone />}
  </>
)

export const AllUsers = () => {
  const service: ServiceName = useServiceName()

  const theme: Theme = useTheme<Theme>()
  const SELECTED_COL: string = theme.palette.action.selected

  const uPool: UserPool = useUserPool()
  const { refreshUsers } = useUserApi()

  const navigate = useNavigate()
  const [query, setQuery] = useUsersQueryState()
  const { filter, order, orderby, poolInfo, role, status } = query

  const [actionMenu, setActionMenu] = useState(null)

  // memoized

  const filteredUsers = useMemo(
    () =>
      uPool
        .filter(u => {
          if (status.length === 0) return false

          const userStatus =
            u.attributes.is_active === 1
              ? LC.USERSTATUS_ACTIVE
              : LC.USERSTATUS_INACTIVE

          return status.includes(userStatus)
        })
        .filter(u => {
          if (role.length === 0) return false

          const ROLE_MAP = {
            [LC.USERROLE_GUEST]: 'guest',
            [LC.USERROLE_USER]: 'user',
            [LC.USERROLE_COACH]: 'coach',
            [LC.USERROLE_ADMIN]: 'admin',
          }

          return role.includes(ROLE_MAP[u.attributes.label])
        })
        .filter(u => {
          if (!filter) return true

          return (
            isFilterMatch(filter, u.attributes.firstname) ||
            isFilterMatch(filter, u.attributes.lastname) ||
            isFilterMatch(filter, u.attributes.email) ||
            isFilterMatch(filter, u.attributes.label) ||
            isFilterMatch(filter, u.attributes.created_at) ||
            isFilterMatch(filter, u.attributes.updated_at) ||
            Number(filter) === Number(u.id)
          )
        }),
    [uPool, status, role, filter]
  )

  const sortedUsers = useMemo(() => {
    const direction = order === DESC ? -1 : 1

    return [...filteredUsers].sort((a, b) => {
      const aVal = a.attributes[orderby]
      const bVal = b.attributes[orderby]

      if (orderby === 'entity_id')
        return (Number(a.id) - Number(b.id)) * direction

      if (orderby === 'created_at' || orderby === 'updated_at') {
        return (
          (new Date(aVal ?? 0).getTime() - new Date(bVal ?? 0).getTime()) *
          direction
        )
      }

      return (
        String(aVal ?? '').localeCompare(String(bVal ?? ''), 'de', {
          sensitivity: 'base',
        }) * direction
      )
    })
  }, [filteredUsers, order, orderby])

  const SEARCH_COUNT = filteredUsers.length

  // functions (a-z)

  const addUser = () => navigate('/users/new')
  const clearSearch = () => setQuery({ filter: '' })
  const closeAM = () => setActionMenu(null)
  const closeStat = () => setQuery({ poolInfo: false })
  const openAM = event => setActionMenu(event.currentTarget)
  const openStat = () => {
    setQuery({ poolInfo: true })
    closeAM()
  }
  const setDomainFilter = filter => setQuery({ filter })
  const setOrderBy = (col: UsersQueryState['orderby']) => {
    if (query.orderby === col)
      setQuery({ order: query.order === 'desc' ? 'asc' : 'desc' })
    else setQuery({ orderby: col, order: 'asc' })
  }
  const setRoleFilter = (_: MouseEvent<HTMLElement>, role: string[] | null) =>
    setQuery({ role: role ?? [] })
  const setSearch = (event: ChangeEvent<HTMLInputElement>) =>
    setQuery({ filter: event.target.value })
  const setStatusFilter = (_, status) => {
    if (status !== null) setQuery({ status })
  }

  // am

  const amData = [
    { caption: 'Neu', icon: <AddTwoTone />, fn: addUser },
    { caption: 'Info', icon: <InfoTwoTone />, fn: openStat },
  ]

  const userLayout = [
    {
      fn: () => setOrderBy('firstname'),
      orderby: 'firstname',
      title: 'Vorname',
      width: 0.2,
    },
    {
      fn: () => setOrderBy('lastname'),
      orderby: 'lastname',
      title: 'Name',
      width: 0.2,
    },
    {
      fn: () => setOrderBy('entity_id'),
      orderby: 'entity_id',
      title: 'ID',
      width: 0.1,
    },
    {
      fn: () => setOrderBy('email'),
      orderby: 'email',
      title: 'E-Mail',
      width: 0.3,
    },
    {
      fn: () => setOrderBy('created_at'),
      orderby: 'created_at',
      title: 'erstellt',
      width: 0.1,
    },
    {
      fn: () => setOrderBy('updated_at'),
      orderby: 'updated_at',
      title: 'aktualisiert',
      width: 0.1,
    },
  ]

  return (
    <KPage
      action={
        <IconButton onClick={openAM}>
          <MoreVertTwoTone />
        </IconButton>
      }
      avatar={
        <Avatar>
          <PeopleTwoTone />
        </Avatar>
      }
      title={service}
    >
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={service}
        onClose={closeAM}
        open={Boolean(actionMenu)}
      >
        {amData.map(am => (
          <AMItem
            caption={am.caption}
            icon={am.icon}
            key={am.caption}
            onClick={am.fn}
          />
        ))}
      </AM>

      <DlgUserPoolInfo open={poolInfo} onClose={closeStat} />

      {/* toolbar */}
      <HStack sx={{ mx: 1 }}>
        <BtnRefresh onClick={refreshUsers} />

        <KTextField
          disabled={uPool.length === 0}
          icon={<SearchTwoTone />}
          onChange={setSearch}
          sx={{ width: 1 }}
          unit={filter && <BtnClearFilter onClick={clearSearch} />}
          value={filter}
        />

        <DomainFilterSelector
          onClear={clearSearch}
          onSelect={setDomainFilter}
          value={filter}
        />

        <UserRoleSelector onChange={setRoleFilter} value={role} />

        <UserStatusSelector onChange={setStatusFilter} value={status} />

        <BtnPoolInfo onClick={openStat} />

        <BtnAddUser onClick={addUser} />
      </HStack>

      {/* preloader */}
      <Collapse in={uPool.length === 0}>
        <LinearProgress sx={{ m: 1 }} />
      </Collapse>

      {/* user pool */}
      <Collapse in={uPool.length > 0}>
        <List
          sx={{
            border: 'solid 1px #CCC',
            m: 1,
            maxHeight: `calc(100vh - 400px)`,
            minHeight: `calc(100vh - 400px)`,
            overflow: 'auto',
            p: 0,
          }}
        >
          <ListSubheader
            sx={{ borderBottom: 'solid 1px #CCC', m: 0, p: 0, zIndex: 2 }}
          >
            <ListItem sx={{ p: 0 }}>
              <ListItemAvatar sx={{ width: 50, ml: 1 }}>&nbsp;</ListItemAvatar>

              {userLayout.map(item => (
                <ListItemText
                  key={item.title}
                  onClick={item.fn}
                  primary={
                    <HStack>
                      <Typography
                        sx={{
                          fontWeight: orderby === item.orderby ? 700 : 400,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <SortArrow order={order} orderby={orderby}>
                        {item.orderby}
                      </SortArrow>
                    </HStack>
                  }
                  sx={{
                    bgcolor:
                      orderby === item.orderby ? SELECTED_COL : 'transparent',
                    cursor: 'pointer',
                    m: 0,
                    p: 1,
                    width: item.width,
                  }}
                />
              ))}
            </ListItem>
          </ListSubheader>

          {sortedUsers.map((user, key) => (
            <UserRow
              key={user.id}
              orderby={orderby}
              sx={{
                bgcolor: key % 2 ? SELECTED_COL : 'background.default',
              }}
            >
              {user}
            </UserRow>
          ))}
        </List>

        <HStack sx={{ mx: 1, mt: 1 }}>
          <HitsInfo count={SEARCH_COUNT} filter={filter} />
        </HStack>
      </Collapse>
    </KPage>
  )
}
